import React from 'react';

export const TOOLBAR_FILTER_WIDTH_PX = 200;

export const TOOLBAR_FILTER_MIN_WIDTH_PX = 160;

export const TOOLBAR_FILL_MIN_WIDTH_PX = 200;

export const TOOLBAR_FILL_BASIS_PX = 260;

const TOOLBAR_GAP_PX = 8;

export interface ToolbarProps {
    children?: React.ReactNode;
    /** Draw a bottom divider — for a bar that sits above scrolling/list content. */
    divider?: boolean;
    /** Padding preset (applies to both axes). `none` for a bar that must sit flush with
     *  the content it pins over — the consumer then owns the gutter. */
    padding?: 'none' | 'compact' | 'default';
    layout?: 'plain' | 'filters';
    className?: string;
    style?: React.CSSProperties;
}

export interface ToolbarFillProps {
    children?: React.ReactNode;
    minWidth?: number;
    className?: string;
    style?: React.CSSProperties;
}

export interface ToolbarItemProps {
    children?: React.ReactNode;
    width?: number;
    minWidth?: number;
    grow?: boolean;
    className?: string;
    style?: React.CSSProperties;
}

const PADDING_CLASSES: Record<NonNullable<ToolbarProps['padding']>, string> = {
    none: '',
    compact: 'px-[12px] py-[8px]',
    default: 'px-[16px] py-[12px]',
};

const ToolbarFill: React.FC<ToolbarFillProps> = ({
    children,
    minWidth = TOOLBAR_FILL_MIN_WIDTH_PX,
    className = '',
    style,
}) => (
    <div
        className={className}
        style={{ flex: `100 1 ${TOOLBAR_FILL_BASIS_PX}px`, minWidth, ...style }}
    >
        {children}
    </div>
);

const ToolbarItem: React.FC<ToolbarItemProps> = ({
    children,
    width = TOOLBAR_FILTER_WIDTH_PX,
    minWidth = TOOLBAR_FILTER_MIN_WIDTH_PX,
    grow = true,
    className = '',
    style,
}) => (
    <div className={className} style={{ flex: `${grow ? 1 : 0} 1 ${width}px`, minWidth, ...style }}>
        {children}
    </div>
);

interface ToolbarComponent extends React.FC<ToolbarProps> {
    Fill: typeof ToolbarFill;
    Item: typeof ToolbarItem;
}

const Toolbar = (({
    children,
    divider = false,
    padding = 'default',
    layout = 'plain',
    className = '',
    style,
}) => {
    const classes = [
        'w-full bg-[var(--malachite-card-background)]',
        PADDING_CLASSES[padding],
        divider ? 'border-0 border-b border-solid border-[var(--malachite-card-border)]' : '',
        className,
    ]
        .filter(Boolean)
        .join(' ');

    const layoutStyle: React.CSSProperties =
        layout === 'filters'
            ? {
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: TOOLBAR_GAP_PX,
              }
            : {};

    return (
        <div className={classes} style={{ ...layoutStyle, ...style }}>
            {children}
        </div>
    );
}) as ToolbarComponent;

Toolbar.Fill = ToolbarFill;
Toolbar.Item = ToolbarItem;

export default Toolbar;
export { Toolbar, ToolbarFill, ToolbarItem };
